import { Directive, ElementRef, forwardRef, OnDestroy, Renderer2 } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

/** Same limit used by Placements, Audio Supply, and Display Supply. */
export const SEARCH_MAX_LENGTH = 255;

export const SEARCH_LIMIT_MESSAGE = `Search cannot exceed ${SEARCH_MAX_LENGTH} characters`;

const LOCAL_MESSAGE = '.display-placement-search, .audio-placement-search, .placements-page__search-wrap';

/**
 * Marks a search over 255 characters invalid and shows the message under the field.
 * Callers skip the request when the control is invalid. The text stays in the field.
 * Auto-applies to toolbar search boxes and search inputs in dialogs.
 */
/* eslint-disable @angular-eslint/directive-selector --
 * Element selectors match existing search inputs so the limit applies
 * without adding an attribute on every template.
 */
@Directive({
  selector: 'input[type="search"], input[matInput][placeholder*="Search"], input[matInput][placeholder*="search"]',
  providers: [{
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => SearchLimitDirective),
    multi: true,
  }],
  standalone: false,
})
/* eslint-enable @angular-eslint/directive-selector */
export class SearchLimitDirective implements Validator, OnDestroy {
  private errorEl: HTMLElement | null = null;
  private anchor: HTMLElement | null = null;
  private slot: HTMLElement | null = null;

  constructor(
    private readonly host: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
  ) {}

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (typeof value === 'string' && value.length > SEARCH_MAX_LENGTH) {
      this.showError();
      return { maxlength: { requiredLength: SEARCH_MAX_LENGTH, actualLength: value.length } };
    }
    this.hideError();
    return null;
  }

  ngOnDestroy(): void {
    this.hideError();
  }

  private showError(): void {
    const input = this.host.nativeElement;
    const field = (input.closest('label, mat-form-field, .mat-mdc-form-field') as HTMLElement | null) || input;
    this.anchor = field;
    this.renderer.addClass(field, 'dxk-search-limit--invalid');
    this.renderer.setAttribute(input, 'aria-invalid', 'true');
    if (input.closest(LOCAL_MESSAGE) || this.errorEl) {
      return;
    }
    const slot = this.ensureSlot(field);
    this.errorEl = this.renderer.createElement('p');
    this.renderer.addClass(this.errorEl, 'dxk-search-limit-error');
    this.renderer.setAttribute(this.errorEl, 'role', 'alert');
    this.renderer.setProperty(this.errorEl, 'textContent', SEARCH_LIMIT_MESSAGE);
    this.renderer.appendChild(slot, this.errorEl);
  }

  /** Keep the message under the bordered field instead of inside the search row. */
  private ensureSlot(field: HTMLElement): HTMLElement {
    const parent = field.parentElement;
    if (parent?.classList.contains('dxk-search-limit')) {
      this.slot = parent;
      return parent;
    }
    const slot = this.renderer.createElement('span');
    this.renderer.addClass(slot, 'dxk-search-limit');
    parent?.insertBefore(slot, field);
    this.renderer.appendChild(slot, field);
    this.slot = slot;
    return slot;
  }

  private hideError(): void {
    const input = this.host.nativeElement;
    if (this.anchor) {
      this.renderer.removeClass(this.anchor, 'dxk-search-limit--invalid');
    }
    if (input.hasAttribute('aria-invalid') && input.getAttribute('aria-invalid') === 'true') {
      this.renderer.removeAttribute(input, 'aria-invalid');
    }
    if (this.errorEl?.parentNode) {
      this.renderer.removeChild(this.errorEl.parentNode, this.errorEl);
    }
    this.errorEl = null;
    this.unwrapSlot();
  }

  private unwrapSlot(): void {
    const slot = this.slot;
    const field = this.anchor;
    const parent = slot?.parentElement;
    if (!slot || !field || !parent || field.parentElement !== slot) {
      this.slot = null;
      return;
    }
    parent.insertBefore(field, slot);
    this.renderer.removeChild(parent, slot);
    this.slot = null;
  }
}
